/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
    Icon,
    FlexRow,
    FlexCol,
    Label,
    Button,
    VisualOnlyLabel,
    withLDConsumer,
} from 'sarsaparilla';
import { gnoment, Gnoment } from 'shared-ui/gnoment';
import SingleDatePickerCalendar from '../single-date-picker-calendar';
import PassInfo from './PassInfo';
import { getFeeByMemberPass, formatFeeDescription } from '../../utils/utilsNew';
import {
    PACK_CREEK_ID,
    MIDDLE_FORK_OF_SALMON_ID,
    SALMON_RIVER_ID,
} from '../../constants/index';

export class GroupMemberItemWithDates extends React.PureComponent {
    static propTypes = {
        index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        member: PropTypes.shape({
            startDate: PropTypes.shape({}),
            endDate: PropTypes.shape({}),
            fName: PropTypes.string,
            mName: PropTypes.string,
            lName: PropTypes.string,
            passholderType: PropTypes.number,
        }),
        permit: PropTypes.shape({
            divisions: PropTypes.shape({
                fees: PropTypes.shape({}),
            }),
            id: PropTypes.string,
            rules: PropTypes.arrayOf(PropTypes.shape({})),
        }).isRequired,
        divisionId: PropTypes.string.isRequired,
        passOptions: PropTypes.object,
        alwaysDisplayPassOptions: PropTypes.bool,
        handleMemberInfoChange: PropTypes.func,
        removeMember: PropTypes.func,
        entryDate: PropTypes.instanceOf(Gnoment),
        dateMap: PropTypes.shape({}),
        focusOnNextAdd: PropTypes.bool,
        disableRemove: PropTypes.bool,
        isDisabled: PropTypes.bool,
        renderDates: PropTypes.bool,
        isFirstNameRequired: PropTypes.bool,
        isLastNameRequired: PropTypes.bool,
        isDateDisabled: PropTypes.bool,
        displayStartDateError: PropTypes.bool,
        displayEndDateError: PropTypes.bool,
        isHighUseFeeApplicable: PropTypes.bool,
        flags: PropTypes.object,
    };

    static defaultProps = {
        isDisabled: true,
        renderDates: true,
    };

    constructor(props) {
        super(props);

        this.state = {
            packCreek: this.props.permit.id === PACK_CREEK_ID,
            packCreekPeakFeeDescription: '$50 Peak Season Fee',
            packCreekShoulderFeeDescription: '$20 Shoulder Season Fee',
        };
    }

    componentDidMount() {
        if (this.props.focusOnNextAdd) {
            this.lNameInput.focus();
        }
    }

    onClickRemove = () => {
        const index = parseInt(this.props.index, 10);
        if (index !== 0) {
            this.props.removeMember(index);
        }
    };

    handleChange(event) {
        const id = event.target.id.replace(/_\d+$/, '');
        const input = event.target.value;
        this.props.handleMemberInfoChange(this.props.index, id, input);
    }

    handleDateChange = (event, pickerId) => {
        const id = pickerId.replace(/_\d+$/, '');
        if (id === 'memberArriving') {
            const { endDate } = this.props.member;
            if (!endDate || !event.isAfter(endDate, 'day')) {
                this.props.handleMemberInfoChange(
                    this.props.index,
                    id,
                    event.startOf('day')
                );
            }
        } else if (id === 'memberDeparting') {
            if (!event.isBefore(this.props.member.startDate, 'day')) {
                this.props.handleMemberInfoChange(
                    this.props.index,
                    id,
                    event.startOf('day')
                );
            }
        }
    };

    calculateStayDays() {
        if (this.props.member.startDate && this.props.member.endDate) {
            const startDate = gnoment.isGnoment(this.props.member.startDate)
                ? this.props.member.startDate
                : gnoment(this.props.member.startDate);
            const endDate = gnoment.isGnoment(this.props.member.endDate)
                ? this.props.member.endDate
                : gnoment(this.props.member.endDate);
            return endDate.diff(startDate, 'days') + 1;
        }
        return 0;
    }

    isDisabled() {
        return this.props.isDisabled && this.props.index === 0;
    }

    isDateMapEmpty() {
        const { dateMap } = this.props;
        if (dateMap) {
            const isEmpty = !Object.keys(dateMap).length;
            return isEmpty;
        }
        return true;
    }

    formatPackCreekFeeDescription = (value) => {
        if (value < 2) {
            this.setState({
                packCreekPeakFeeDescription: '$50 Peak Season Fee',
                packCreekShoulderFeeDescription: '$20 Shoulder Season Fee',
            });
        } else {
            this.setState({
                packCreekPeakFeeDescription: '$25 Peak Season Fee',
                packCreekShoulderFeeDescription: '$10 Shoulder Season Fee',
            });
        }
    };

    renderRemoveMemberButton() {
        if (this.isDisabled() || this.props.disableRemove) {
            return null;
        }

        return (
            <Button
                type="button"
                appearance="subtle-danger"
                className="removeMemberLink"
                screenReaderTextBefore={`Remove member ${
                    this.props.index + 1
                } from the group member list`}
                onClick={this.onClickRemove}
                iconBeforeElement={<Icon iconName="delete" />}
            />
        );
    }

    renderNameRow() {
        return (
            <FlexRow>
                <FlexCol md={6}>
                    <div className="rec-form-item-wrap">
                        <Label
                            htmlFor={`memberLName_${this.props.index}`}
                            isRequired={this.props.isLastNameRequired}
                        >
                            Last Name
                        </Label>
                        <input
                            id={`memberLName_${this.props.index}`}
                            className="memberLNameInput"
                            type="text"
                            readOnly={this.isDisabled()}
                            disabled={this.isDisabled()}
                            value={this.props.member.lName}
                            ref={(input) => {
                                this.lNameInput = input;
                            }}
                            onChange={(e) => {
                                this.handleChange(e);
                            }}
                        />
                    </div>
                </FlexCol>
                <FlexCol md={4}>
                    <div className="rec-form-item-wrap">
                        <Label
                            htmlFor={`memberFName_${this.props.index}`}
                            isRequired={this.props.isFirstNameRequired}
                        >
                            First Name
                        </Label>
                        <input
                            id={`memberFName_${this.props.index}`}
                            className="memberFNameInput"
                            type="text"
                            readOnly={this.isDisabled()}
                            disabled={this.isDisabled()}
                            value={this.props.member.fName}
                            onChange={(e) => {
                                this.handleChange(e);
                            }}
                        />
                    </div>
                </FlexCol>
                <FlexCol md={2}>
                    <div className="rec-form-item-wrap">
                        <Label htmlFor={`memberMName_${this.props.index}`}>MI</Label>
                        <input
                            id={`memberMName_${this.props.index}`}
                            className="memberMNameInput"
                            type="text"
                            readOnly={this.isDisabled()}
                            disabled={this.isDisabled()}
                            maxLength="1"
                            value={this.props.member.mName}
                            onChange={(e) => {
                                this.handleChange(e);
                            }}
                        />
                    </div>
                </FlexCol>
            </FlexRow>
        );
    }

    renderStayAndFeeRow() {
        const fees = get(this.props.permit.divisions[this.props.divisionId], 'fees', []);
        const disableCalendar = this.props.isDateDisabled || this.isDisabled();
        // Middle fork and Salmon have a special date case for their group member selections
        const specialPermit =
            this.props.permit.id === MIDDLE_FORK_OF_SALMON_ID ||
            this.props.permit.id === SALMON_RIVER_ID;

        return (
            <FlexRow>
                {this.props.renderDates && (
                    <FlexCol md={3}>
                        <div className="rec-form-item-wrap">
                            <label htmlFor={`memberArriving_${this.props.index}`}>
                                Arriving
                            </label>
                            <div
                                className={
                                    this.props.displayStartDateError ? 'rec-error' : ''
                                }
                            >
                                <SingleDatePickerCalendar
                                    id={`memberArriving_${this.props.index}`}
                                    permit={this.props.permit}
                                    selectedDate={this.props.member.startDate}
                                    handleDateChange={this.handleDateChange}
                                    dateMap={this.props.dateMap}
                                    initialVisibleMonth={this.props.entryDate}
                                    ignoreQuotaRestrictions
                                    readOnly={this.isDisabled()}
                                    disabled={disableCalendar}
                                    renderLabels={false}
                                    blockLotteryDates
                                />
                            </div>
                        </div>
                    </FlexCol>
                )}
                {this.props.renderDates && (
                    <FlexCol md={3}>
                        <div className="rec-form-item-wrap">
                            <label htmlFor={`memberDeparting_${this.props.index}`}>
                                Departing
                            </label>
                            <div
                                className={
                                    this.props.displayEndDateError ||
                                    (this.isDateMapEmpty() && specialPermit)
                                        ? 'rec-error'
                                        : ''
                                }
                            >
                                <SingleDatePickerCalendar
                                    id={`memberDeparting_${this.props.index}`}
                                    permit={this.props.permit}
                                    selectedDate={this.props.member.endDate}
                                    handleDateChange={this.handleDateChange}
                                    dateMap={this.props.dateMap}
                                    initialVisibleMonth={this.props.entryDate}
                                    ignoreQuotaRestrictions
                                    readOnly={this.isDisabled()}
                                    disabled={disableCalendar}
                                    renderLabels={false}
                                    blockLotteryDates
                                />
                                {this.isDateMapEmpty() && specialPermit && (
                                    <div>
                                        You must enter the overall trip’s exit date before
                                        you can enter one for a group member.
                                    </div>
                                )}
                            </div>
                        </div>
                    </FlexCol>
                )}
                <FlexCol md={2}>
                    <div className="rec-form-item-wrap">
                        <VisualOnlyLabel htmlFor={`memberDays_${this.props.index}`}>
                            Days
                        </VisualOnlyLabel>
                        <div
                            id={`memberDays_${this.props.index}`}
                            className="per-static-form-item"
                        >
                            {this.calculateStayDays()}
                        </div>
                    </div>
                </FlexCol>
                <FlexCol md={4}>
                    <div className="rec-form-item-wrap">
                        <VisualOnlyLabel
                            htmlFor={`memberFeeTypeValue_${this.props.index}`}
                        >
                            Fee per Day
                        </VisualOnlyLabel>
                        <div
                            id={`memberFeeTypeValue_${this.props.index}`}
                            className="per-static-form-item"
                        >
                            {formatFeeDescription(
                                getFeeByMemberPass(
                                    this.props.member.passholderType,
                                    fees,
                                    this.props.isHighUseFeeApplicable
                                )
                            )}
                        </div>
                    </div>
                </FlexCol>
            </FlexRow>
        );
    }

    render() {
        return (
            <div className="per-form-group-item">
                <div className="per-form-group-item-header">
                    <div className="per-form-group-item-title">
                        Member {this.props.index + 1}
                    </div>
                    {this.renderRemoveMemberButton()}
                </div>
                {this.renderNameRow()}
                {this.renderStayAndFeeRow()}
                <PassInfo
                    index={this.props.index}
                    member={this.props.member}
                    options={this.props.passOptions}
                    packCreek={this.state.packCreek}
                    alwaysDisplayPassOptions={this.props.alwaysDisplayPassOptions}
                    handleMemberInfoChange={this.props.handleMemberInfoChange}
                    formatPackCreekFeeDescription={this.formatPackCreekFeeDescription}
                />
            </div>
        );
    }
}

export default withLDConsumer()(GroupMemberItemWithDates);
